import type { ForecastQuery } from 'routes/forecast/redux/modules/forecast.types';
import type { Team } from 'types/team';
import { dayTools } from 'utils/time';
import { UNASSIGNED_FORECAST_GROUP_KEY } from '../redux/modules/forecast.constants';
import type { EnrichedForecast, Forecast, ForecastAssignee } from '../types/forecast';

export interface ForecastsSummary {
  /** Total estimated minutes */
  total_estimated_minutes: number;
  /** Total of progress (e.g, work) already done */
  total_progressed_minutes: number;
  /** % of completion */
  total_percent: number;
  /** Number of tasks */
  task_count: number;
}
export const calculateForecastTotalsForAssignee = (
  assigneeId: number | string,
  forecasts: Array<EnrichedForecast>
): ForecastsSummary => {
  return calculateForecastTotals(id => id === assigneeId, forecasts);
};
export const calculateForecastTotalsForProject = (
  forecasts: Array<EnrichedForecast>
): ForecastsSummary => {
  return calculateForecastTotals(_ => true, forecasts);
};
const calculateForecastTotals = (
  matchAssigneeId: (id: number) => boolean,
  forecasts: Array<EnrichedForecast>
): ForecastsSummary => {
  return forecasts.reduce(
    (sums: ForecastsSummary, current): ForecastsSummary => {
      sums.task_count += 1;

      if (current.completed_at) return sums;

      const allocationEntries = Object.entries(current.allocationByUser).filter(([userId]) =>
        matchAssigneeId(+userId)
      );
      // In projects view, we include data from all assigned users, so we get divisor 1
      // In assignee view, we only include data from the current user, so we get divisor 1/(number of users)
      const divisor = (current.users.length || 1) / (allocationEntries.length || 1);

      sums.total_estimated_minutes += current.estimated_minutes / divisor;
      sums.total_progressed_minutes += current.logged_duration.total_minutes / divisor;
      sums.total_percent = Math.round(
        (sums.total_progressed_minutes / sums.total_estimated_minutes) * 100
      );

      return sums;
    },
    {
      total_estimated_minutes: 0,
      total_progressed_minutes: 0,
      total_percent: 0,
      task_count: 0,
    }
  );
};

export interface AssigneeForecastGroup {
  assignee: ForecastAssignee | null;
  assignee_summary: ForecastsSummary;
  forecasts: Array<EnrichedForecast>;
}

export const generateAssigneeGroupsFromForecasts = (
  forecasts: Array<EnrichedForecast>
): Array<AssigneeForecastGroup> => {
  const { forecastsByAssigneeId, assigneeById } = forecasts.reduce(
    (map, forecast) => {
      if (forecast.users.length === 0) {
        map.forecastsByAssigneeId.set(UNASSIGNED_FORECAST_GROUP_KEY, [
          ...(map.forecastsByAssigneeId.get(UNASSIGNED_FORECAST_GROUP_KEY) ?? []),
          forecast,
        ]);
      } else {
        forecast.users.forEach(user => {
          map.forecastsByAssigneeId.set(user.id, [
            ...(map.forecastsByAssigneeId.get(user.id) ?? []),
            forecast,
          ]);
          map.assigneeById.set(user.id, user);
        });
      }
      return map;
    },
    {
      forecastsByAssigneeId: new Map<number | string, Array<EnrichedForecast>>(),
      assigneeById: new Map<number | string, ForecastAssignee | null>(),
    }
  );

  return [...forecastsByAssigneeId.entries()].map(([assigneeId, forecastsOnAssignee]) => {
    return {
      assignee: assigneeById.get(assigneeId) ?? null,
      assignee_summary: calculateForecastTotalsForAssignee(assigneeId, forecastsOnAssignee),
      forecasts: forecastsOnAssignee,
    };
  });
};

export function sortForecastList(a: Forecast, b: Forecast) {
  const aStart = new Date(a.from);
  const bStart = new Date(b.from);
  const overlaps = dayTools.format(a.from) === dayTools.format(b.from);
  if (overlaps) {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  }
  return aStart.getTime() - bStart.getTime();
}

export function filterForecastListForUser(
  forecast: EnrichedForecast,
  options: {
    userId: number;
    from?: string;
    to?: string;
    excludeCompletedForecasts?: boolean;
    hasOverlap?: boolean;
  }
) {
  const { from: start_date, to: due_date, users: assignees } = forecast;
  const { from, to, userId, hasOverlap } = options;
  if (options.excludeCompletedForecasts && forecast.completed_at != null) {
    return false;
  }

  if (!assignees.find(user => user.id === userId)) {
    return false;
  }

  if (!forecast.project?.active) {
    return false;
  }

  if (from && to) {
    if (hasOverlap) {
      if (!(start_date <= to && due_date >= from)) {
        return false;
      }
    } else {
      const withinRange =
        (due_date >= from && due_date <= to) || (start_date >= from && start_date <= to);
      if (!withinRange) {
        return false;
      }
    }
  }

  return true;
}

interface FilterForecastsQuery<TForecast> {
  query: ForecastQuery;
  forecasts: Array<TForecast>;
  teams: Array<Team>;
  onlyMarkAsFiltered?: boolean;
}
export function filterForecastsByQuery<TForecast extends Forecast>(
  props: FilterForecastsQuery<TForecast>
): Array<TForecast & { hiddenByFilter?: boolean }> {
  const { projectIds, showCompleted, since, until } = props.query;
  const queryUsers = getFinalForecastQueryUserIds(props.query, props.teams);

  const filterForecasts = (forecast: TForecast) => {
    const { project, users: assignees } = forecast;
    const doesNotMatchQueryStatus = !showCompleted && forecast.completed_at;
    // For forecast, projectIds is always Array<number>
    const doesNotMatchQueryProjectIds =
      project && projectIds.length > 0 && !projectIds.includes(project.id);

    if (doesNotMatchQueryProjectIds || doesNotMatchQueryStatus) {
      return false;
    }

    // unless we're specifically querying for it, don't return tasks that belong to archived projects
    if (!projectIds.length && !project?.active) {
      return false;
    }

    // Filter by date range
    if (since && until) {
      const forecastStart = forecast.from;
      const forecastEnd = forecast.to;
      // Check if forecast overlaps with the selected date range
      const hasOverlap = forecastStart <= until && forecastEnd >= since;
      if (!hasOverlap) {
        return false;
      }
    }

    const showAllUsers = queryUsers.size === 0;
    const showUnassigned = queryUsers.has(null);
    const isForecastUnassigned = assignees.length === 0;
    const isUserPresent = assignees.some(({ id }) => !!id && queryUsers.has(id));
    const isMatchingQueryUsers = isUserPresent || (isForecastUnassigned && showUnassigned);
    return showAllUsers || isMatchingQueryUsers;
  };

  if (props.onlyMarkAsFiltered) {
    return props.forecasts.map(forecast => ({
      ...forecast,
      hiddenByFilter: !filterForecasts(forecast),
    }));
  }
  return props.forecasts.filter(filterForecasts);
}

export function getFinalForecastQueryUserIds(
  query: ForecastQuery,
  teams: Array<Team>
): Set<number | null> {
  if (query.teamIds != null) {
    return new Set(
      query.teamIds.flatMap(teamId => teams.find(team => team.id === teamId)?.user_ids ?? [])
    );
  }
  return new Set(query.userIds ?? []);
}
